import React from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@material-ui/core";
import PropTypes from "prop-types";
import ModuleSwitchGrid from "../ModuleSwitchGrid";
import {
  SELECTION_EXISTING,
  SELECTION_NEW,
  describeSelection,
  findBundleById,
  matchedBundleBannerMessage,
  matchedBundleFieldError,
  moduleIdsForBundle,
  toggleModuleId,
} from "../../utils/bundle-modules";
import {
  CATALOGUE_UNAVAILABLE_MESSAGE,
  NO_BUNDLE,
  NO_BUNDLES_HINT,
  NO_MODULES_MESSAGE,
  describeCatalogue,
} from "../../utils/catalogue-state";

// "Which bundle is this company on, and which modules does it get" — the whole editable
// answer, in one widget.
//
// Two surfaces ask that question: the standalone company bundle dialog and the Company
// Features panel inside the company details dialog. They used to answer it with two
// different bodies (one editable, one a read-only grid with a hardcoded disabled switch),
// which is precisely how an admin ends up unable to edit a bundle in one place and able to
// in another. There is one body now; the callers only supply chrome.
//
// The wizard's Access Rights step deliberately keeps its own chrome (white fields on the
// dark step background, approval-specific wording) but shares the same set math, the same
// grid and — since it had drifted back onto a length-based one — the same loaded/empty gate.
export { NO_BUNDLE };

// The company payload still carries the legacy `featureGroupId` name on the read alias, so
// accept either and normalise "nothing assigned" to -1.
export const currentBundleId = (companyFeature) => {
  const raw = companyFeature.bundleId !== undefined
    ? companyFeature.bundleId
    : companyFeature.featureGroupId;
  return raw === undefined || raw === null ? NO_BUNDLE : raw;
};

// The Formik seed both surfaces start from: the bundle the company holds, with that bundle's
// modules switched on. bundle-options may still be in flight — `enableReinitialize` re-seeds
// when it lands.
//
// The seed is normalised onto the LIVE option's own id when one matches. bundle-options casts
// ids to int while the company-feature read passes the driver's value through, so a company
// on bundle "10" would otherwise carry a value that matches no <MenuItem value={10}> and the
// Select would render blank for a perfectly live bundle.
export const bundleSelectionInitialValues = (bundleOptions, companyFeature) => {
  const raw = currentBundleId(companyFeature);
  const live = findBundleById(bundleOptions, raw);
  const bundle = live ? live.id : raw;
  return {
    bundle,
    moduleIds: moduleIdsForBundle(bundleOptions, bundle),
    newBundleName: "",
  };
};

// A company can only ever be put on a real bundle, so a selection that matches nothing has to
// be created first — and a bundle needs a name.
//
// And a selection that matches a DIFFERENT live bundle blocks: see the invariant in
// bundle-modules. Formik never reaches onSubmit while a validate() returns errors, so this is
// what stops both company surfaces committing a bundle the dropdown is not showing. The error
// sits on `bundle` because the dropdown is the control that has to change — Formik touches
// every field on a submit attempt, so it surfaces under the dropdown the moment Save is
// pressed, next to the banner that has been on screen since the switches drifted.
export const validateBundleSelection = (bundleOptions) => (values) => {
  const errors = {};
  const { status, matched } = describeSelection(
    bundleOptions,
    values.bundle,
    values.moduleIds
  );
  if (status === SELECTION_NEW && !(values.newBundleName || "").trim()) {
    errors.newBundleName = "Name is required";
  }
  if (status === SELECTION_EXISTING) {
    errors.bundle = matchedBundleFieldError(matched);
  }
  return errors;
};

export default function BundleSelectionEditor({
  formik,
  bundleOptions,
  moduleListing,
  isLoaded,
  isLoading,
  hasFailed,
  onRetry,
  disabled,
}) {
  const bundleId = formik.values.bundle;
  const moduleIds = formik.values.moduleIds || [];

  // Nothing on this panel may assert anything until BOTH catalogues have actually been
  // fetched — the shared gate, so the wizard cannot answer this differently.
  const {
    isReady,
    hasFailedToLoad,
    hasNoBundles,
    hasNoModules,
  } = describeCatalogue({
    isLoaded,
    isLoading,
    hasFailed,
    bundleOptions,
    moduleListing,
  });

  const skeleton = (
    <>
      <Skeleton variant="text" animation="wave" height={25} />
      <Skeleton variant="text" animation="wave" height={25} />
      <Skeleton variant="text" animation="wave" height={25} />
      <Skeleton variant="text" animation="wave" height={25} />
    </>
  );

  // Waiting is only honest while something is still coming. When the catalogue read has
  // failed and nothing ever arrived, the skeleton would sit there for good — so it is
  // replaced by what happened and, where the caller can offer one, a way to try again.
  const waiting = hasFailedToLoad ? (
    <Alert
      severity="warning"
      action={onRetry && (
        <Button color="inherit" size="small" onClick={onRetry}>
          Retry
        </Button>
      )}
    >
      {CATALOGUE_UNAVAILABLE_MESSAGE}
    </Alert>
  ) : skeleton;

  // The whole catalogue, with the chosen bundle's modules switched on. Picking a bundle
  // re-seeds the switches from it and drops any half-typed new bundle name.
  //
  // ONE setValues, never three setFieldValue calls.
  //
  // Formik 2.4.5 validates a setFieldValue against `setIn(state.values, field, value)`, where
  // `state.values` belongs to the render whose closure the (useEventCallback-wrapped) handler
  // is running — and Formik's dispatch REPLACES that object rather than mutating it, while
  // useEventCallback only swaps in a fresher closure from a layout effect, i.e. after a
  // commit. Inside a React event handler every update is batched, so no commit happens
  // between the three calls: all three validate the same pre-click values with one field
  // patched into each, and never the selection the admin actually made.
  //
  // For "Use it" that meant all three saw SELECTION_EXISTING and each re-set errors.bundle.
  // The click swapped in the matched bundle and removed the banner — and with it the button —
  // leaving the red "choose bundle X" under a dropdown that was by then showing bundle X, and
  // no affordance left to clear it. setValues validates the whole object it was handed, so
  // validation only ever sees a coherent selection.
  //
  // Do not "simplify" this back into setFieldValue calls after checking it in isolation: the
  // same three calls made outside React's batching (from a promise callback, say) re-render
  // between each and clear the error correctly. Only a real click reproduces it.
  const selectBundle = (id) => {
    formik.setValues((previous) => ({
      ...previous,
      bundle: id,
      moduleIds: moduleIdsForBundle(bundleOptions, id),
      newBundleName: "",
    }));
  };

  const renderModules = () => {
    // The company's own bundle id comes from the company-feature read, which both callers
    // wait for before rendering this editor at all — so "nothing assigned" is known without
    // either catalogue. The None case draws nothing here: its caption + read-only effective-
    // access tree (Decision #19) are rendered as a sibling by the tab, so drawing a second
    // caption here would stack two overlapping "no bundle assigned" lines. The Select above
    // remains the only live control for the None state.
    if (bundleId === NO_BUNDLE || bundleId === undefined) {
      return null;
    }
    if (!isReady) {
      return waiting;
    }

    // bundle-options only lists LIVE + ACTIVE bundles. A company assigned a
    // bundle that was later deactivated still holds it, so we cannot resolve its
    // modules — say that, instead of showing a grid with everything switched off.
    //
    // Reachable once both catalogues have been FETCHED — including when the fetch came back
    // empty, which is a system with no live bundles at all and therefore the strongest case
    // of this, not an excuse to suppress the message. It is still unreachable while nothing
    // has loaded, and the lookup goes through the shared sameId, so neither "still loading"
    // nor a 10-vs-"10" mismatch can be mistaken for a retired bundle.
    if (!findBundleById(bundleOptions, bundleId)) {
      return (
        <Typography variant="caption" color="textSecondary">
          This company is on a bundle that is no longer active (id {bundleId}).
          Its existing access rights are unchanged.
          {hasNoBundles && NO_BUNDLES_HINT}
        </Typography>
      );
    }

    // A live bundle whose modules cannot be drawn, because the catalogue came back empty.
    if (hasNoModules) {
      return (
        <Typography variant="caption" color="textSecondary">
          {NO_MODULES_MESSAGE}
        </Typography>
      );
    }

    const selection = describeSelection(bundleOptions, bundleId, moduleIds);

    return (
      <>
        {/* Not an FYI about something the save is about to do on its own — this state
            BLOCKS, so it is a warning, and "Use it" is the one-click way forward (it puts
            the matched bundle in the dropdown, which makes the selection unchanged). The
            banner is the blocked state made visible in the body: it is on screen from the
            moment the switches drift, not only once the button has been pressed. */}
        {selection.status === SELECTION_EXISTING && (
          <Box mb={2}>
            <Alert
              severity="warning"
              action={(
                <Button
                  color="inherit"
                  size="small"
                  disabled={disabled}
                  onClick={() => selectBundle(selection.matched.id)}
                >
                  Use it
                </Button>
              )}
            >
              {matchedBundleBannerMessage(selection.matched)}
            </Alert>
          </Box>
        )}
        {selection.status === SELECTION_NEW && (
          <Box mb={2}>
            <TextField
              fullWidth
              label="New bundle name"
              name="newBundleName"
              placeholder="e.g. Enterprise + PRIFE"
              required
              variant="outlined"
              value={formik.values.newBundleName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={disabled}
              error={Boolean(formik.touched.newBundleName && formik.errors.newBundleName)}
              helperText={
                (formik.touched.newBundleName && formik.errors.newBundleName)
                || "No bundle grants this combination. Saving creates it as a new bundle and assigns that bundle to this company."
              }
            />
          </Box>
        )}
        <ModuleSwitchGrid
          moduleListing={moduleListing}
          moduleIds={moduleIds}
          onToggle={(id) => formik.setFieldValue(
            "moduleIds",
            toggleModuleId(moduleIds, id)
          )}
          disabled={disabled}
        />
      </>
    );
  };

  // This dropdown names the bundle the company will be put on — so when the form refuses to
  // save, the reason belongs here, on the control that has to change.
  const bundleError = (formik.touched.bundle && formik.errors.bundle) || "";

  return (
    <>
      <Box pb={2}>
        <FormControl fullWidth error={Boolean(bundleError)}>
          <InputLabel>Bundle</InputLabel>
          <Select
            value={bundleId === undefined ? NO_BUNDLE : bundleId}
            onChange={(event) => selectBundle(event.target.value)}
            label="Bundle"
            fullWidth
            disabled={disabled}
          >
            <MenuItem value={NO_BUNDLE}>
              <em>None</em>
            </MenuItem>
            {(bundleOptions || []).map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
          {bundleError && <FormHelperText>{bundleError}</FormHelperText>}
        </FormControl>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2">
          <b>Modules this company will get</b>
        </Typography>
        {/* The whole catalogue is on screen, so the honest number is how much of it is
            switched on — not "what the bundle carries". Withheld until both lists have
            landed: "0 of 0" / "0 of N" while bundle-options is still in flight is the same
            lie the body used to tell. */}
        {isReady && (
          <Typography variant="caption" color="textSecondary">
            {moduleIds.length}
            {" of "}
            {(moduleListing || []).length}
            {" module(s) on"}
          </Typography>
        )}
      </Box>

      <Box pt={2}>{renderModules()}</Box>
    </>
  );
}

BundleSelectionEditor.propTypes = {
  formik: PropTypes.object.isRequired,
  bundleOptions: PropTypes.array,
  moduleListing: PropTypes.array,
  // Both catalogues have come back at least once. Not "are non-empty".
  isLoaded: PropTypes.bool,
  isLoading: PropTypes.bool,
  // A catalogue read was rejected. Only acted on while nothing has ever loaded.
  hasFailed: PropTypes.bool,
  onRetry: PropTypes.func,
  disabled: PropTypes.bool,
};
